import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import { useDebounce } from '@/hooks';
import { useGetListCourierQuery, useUpdateCourierNameMutation } from '@/redux/api/api.caller';
import commonSlice from '@/redux/features/common.slice';
import shipmentSlice, { courierModalSelector } from '@/redux/features/shipment.slice';
import { ICourier } from '@/types/settings';
import { BlockStack, EmptyState, Grid, Icon, InlineStack, Modal, Spinner, TextField } from '@shopify/polaris';
import { DeliveryIcon } from '@shopify/polaris-icons';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CourierItemImageStyled, CourierItemStyled } from './styled';

interface CourierModalProps {
  clearSelection?: () => void;
}

const CourierModal = ({ clearSelection }: CourierModalProps) => {
  const [selectedCourier, setSelectedCourier] = useState<ICourier | undefined>(undefined);
  const [search, setSearch] = useState<string>('');
  const [displayedCount, setDisplayedCount] = useState(10); // Số lượng items hiển thị

  const debouncedSearch = useDebounce(search, 500);
  const observerRef = useRef<IntersectionObserver>();

  const { data, isLoading } = useGetListCourierQuery({ page: 1, perPage: 500 });

  const [apiError, setApiError] = useState<string>('');
  const dispatch = useDispatch();
  const courierModal = useSelector(courierModalSelector);

  const [updateCourierName, { isLoading: isEditLoading }] = useUpdateCourierNameMutation();

  // Filter couriers based on search
  const filteredCouriers = useMemo(() => {
    const couriers = data?.data?.couriers || [];
    if (!debouncedSearch.trim()) return couriers;

    return couriers.filter(
      (courier) =>
        courier.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        courier.display_name.toLowerCase().includes(debouncedSearch.toLowerCase()),
    );
  }, [data?.data?.couriers, debouncedSearch]);

  // Couriers to display (with pagination)
  const displayedCouriers = useMemo(() => {
    return filteredCouriers.slice(0, displayedCount);
  }, [filteredCouriers, displayedCount]);

  // Check if has more data
  const hasMoreData = displayedCount < filteredCouriers.length;

  // Reset displayed count when search changes
  useEffect(() => {
    setDisplayedCount(10);
  }, [debouncedSearch]);

  // Load more items
  const loadMore = useCallback(() => {
    if (hasMoreData) {
      setDisplayedCount((prev) => Math.min(prev + 10, filteredCouriers.length));
    }
  }, [hasMoreData, filteredCouriers.length]);

  // Intersection Observer callback
  const lastCourierElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMoreData) {
          loadMore();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isLoading, hasMoreData, loadMore],
  );

  const handleCloseModal = () => {
    setApiError('');
    setSearch('');
    setDisplayedCount(10);
    setSelectedCourier(undefined);
    dispatch(shipmentSlice.actions.handleChangeCourierModal({ ...courierModal, open: false, value: '' }));
  };

  const handleEditCourierName = () => {
    if (!selectedCourier) return;

    updateCourierName({ ids: courierModal.id, courier: selectedCourier.display_name, courierCode: selectedCourier.name }).then(
      (res) => {
        if ('data' in res && res.data && res.data?.state === 1) {
          handleCloseModal();
          clearSelection?.();
          dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
          if (apiError) {
            setApiError('');
          }
          return;
        }
        setApiError(getErrorFromAPI(res));
      },
    );
  };

  const handleCourierSelect = (courier: ICourier) => {
    setSelectedCourier(courier);
    dispatch(
      shipmentSlice.actions.handleChangeCourierModal({
        ...courierModal,
        value: courier.display_name,
      }),
    );
  };

  return (
    <Modal
      open={courierModal.open}
      onClose={handleCloseModal}
      title="Edit carrier"
      primaryAction={{
        content: 'Save',
        onAction: handleEditCourierName,
        disabled: !selectedCourier,
        loading: isEditLoading,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: handleCloseModal,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap={'400'}>
          <TextField
            label=""
            placeholder="Search a carrier name"
            value={search}
            onChange={setSearch}
            disabled={isLoading}
            autoComplete="off"
          />

          {isLoading ? (
            <InlineStack align="center">
              <Spinner size="small" />
            </InlineStack>
          ) : displayedCouriers.length > 0 ? (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Grid>
                {displayedCouriers.map((courier, index) => {
                  const isLast = index === displayedCouriers.length - 1;

                  return (
                    <Grid.Cell key={courier.id} columnSpan={{ xs: 3, sm: 2, md: 2, lg: 4, xl: 4 }}>
                      <CourierItemStyled
                        $active={selectedCourier?.id === courier.id}
                        onClick={() => handleCourierSelect(courier)}
                        ref={isLast && hasMoreData ? lastCourierElementRef : undefined}
                      >
                        <CourierItemImageStyled>
                          {courier.logo ? <img src={courier.logo} alt={courier.name} /> : <Icon source={DeliveryIcon} />}
                        </CourierItemImageStyled>
                        {courier.display_name}
                      </CourierItemStyled>
                    </Grid.Cell>
                  );
                })}
              </Grid>
            </div>
          ) : (
            <EmptyState
              heading="Select your carrier"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>No carriers found.</p>
            </EmptyState>
          )}

          {apiError && <div style={{ color: 'red', fontSize: '14px' }}>{apiError}</div>}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default memo(CourierModal);
